import { useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

// Port of src/app/directives/multiline-ellipsis.directive.ts ([multilineEllipsis]).
//
// Directive behavior (mirrored exactly):
// - Truncation test: element.scrollHeight > (computed line-height * 2) — i.e.
//   the content needs more than the 2 clamped lines (pairs with the
//   .multiline-ellipsis class which uses -webkit-line-clamp: 2).
// - When truncated, sets element.title = element.textContent; otherwise
//   removes the title attribute.
// - Re-checks on: MutationObserver (childList + subtree + characterData),
//   'mouseenter' on the element, and window 'resize'. Initial check on mount.
// - Additionally exposes the truncation state as a boolean for React consumers.

export function useMultilineEllipsis<T extends HTMLElement>(): {
  ref: RefObject<T | null>;
  isEllipsis: boolean;
} {
  const ref = useRef<T>(null);
  const [isEllipsis, setIsEllipsis] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkEllipsis = () => {
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
      const maxHeight = lineHeight * 2; // Adjust this for the number of lines you want to clamp

      const truncated = element.scrollHeight > maxHeight;
      if (truncated) {
        element.title = element.textContent ?? '';
      } else {
        element.removeAttribute('title');
      }
      setIsEllipsis(truncated);
    };

    const mutationObserver = new MutationObserver(checkEllipsis);
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      characterData: true
    });

    element.addEventListener('mouseenter', checkEllipsis);
    window.addEventListener('resize', checkEllipsis);

    checkEllipsis();

    return () => {
      mutationObserver.disconnect();
      element.removeEventListener('mouseenter', checkEllipsis);
      window.removeEventListener('resize', checkEllipsis);
    };
  }, []);

  return { ref, isEllipsis };
}
